import React from 'react';
import { Smartphone, Download } from 'lucide-react';
import { Button } from './ui/button';

const MobileApp = () => {
  return (
    <section className="py-24 bg-gradient-to-b from-slate-900 to-slate-950">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="relative bg-gradient-to-br from-slate-800/50 to-slate-900/50 border border-slate-700 rounded-3xl p-8 md:p-12 lg:p-16 overflow-hidden">
          {/* Background Decorations */}
          <div className="absolute top-0 right-0 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-80 h-80 bg-blue-600/10 rounded-full blur-3xl" />
          
          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Content */}
            <div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
                Access XPO Anytime, Anywhere
              </h2>
              <p className="text-gray-400 text-lg mb-8 leading-relaxed">
                Stay connected and manage your investments on the go with our intuitive 
                mobile app. Experience seamless trading, real-time updates, and full 
                control at your fingertips.
              </p>
              
              {/* Features List */}
              <ul className="space-y-4 mb-8">
                {[
                  'Real-time market data & notifications',
                  'Secure biometric authentication',
                  'Instant fund transfers & withdrawals',
                  'Portfolio tracking & analytics'
                ].map((feature, index) => (
                  <li key={index} className="flex items-center space-x-3">
                    <div className="w-5 h-5 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-full flex items-center justify-center">
                      <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="text-gray-300">{feature}</span>
                  </li>
                ))}
              </ul>
              
              {/* Download Button */}
              <Button
                size="lg"
                className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white px-8 py-6 rounded-xl"
              >
                <Download className="w-5 h-5 mr-2" />
                Download for Android
              </Button>
            </div>
            
            {/* Phone Mockup */}
            <div className="relative flex justify-center lg:justify-end">
              <div className="relative">
                {/* Phone Frame */}
                <div className="relative w-64 h-[520px] bg-gradient-to-br from-slate-700 to-slate-900 rounded-[3rem] p-2 shadow-2xl shadow-cyan-500/10">
                  <div className="w-full h-full bg-slate-950 rounded-[2.5rem] overflow-hidden relative">
                    {/* Notch */}
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-6 bg-slate-900 rounded-b-xl" />
                    
                    {/* Screen Content */}
                    <div className="w-full h-full pt-8 bg-gradient-to-br from-slate-900 to-slate-950 flex flex-col">
                      {/* Header */}
                      <div className="px-4 py-3 flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <div className="w-8 h-8 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg flex items-center justify-center">
                            <span className="text-white font-bold text-sm">X</span>
                          </div>
                          <span className="text-white font-semibold">XPO</span>
                        </div>
                        <Smartphone className="w-5 h-5 text-gray-500" />
                      </div>
                      
                      {/* Portfolio Value */}
                      <div className="px-4 py-4">
                        <p className="text-gray-500 text-xs">Total Portfolio Value</p>
                        <p className="text-2xl font-bold text-white">$47,329.52</p>
                        <span className="text-emerald-400 text-sm">+3.24%</span>
                      </div>
                      
                      {/* Chart Placeholder */}
                      <div className="px-4 flex-1">
                        <div className="w-full h-32 bg-slate-800/50 rounded-xl flex items-end p-3 space-x-1">
                          {[40, 65, 55, 80, 70, 90, 75, 85, 95].map((height, i) => (
                            <div
                              key={i}
                              className="flex-1 bg-gradient-to-t from-cyan-500 to-blue-500 rounded-t"
                              style={{ height: `${height}%` }}
                            />
                          ))}
                        </div>
                      </div>
                      
                      {/* Bottom Nav */}
                      <div className="px-4 py-4 flex justify-around border-t border-slate-800">
                        {['Home', 'Trade', 'Portfolio', 'More'].map((item, i) => (
                          <div key={i} className="flex flex-col items-center">
                            <div className={`w-6 h-6 rounded-full ${i === 0 ? 'bg-cyan-500' : 'bg-slate-700'}`} />
                            <span className={`text-xs mt-1 ${i === 0 ? 'text-cyan-400' : 'text-gray-500'}`}>{item}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Decorative Elements */}
                <div className="absolute -top-4 -right-4 w-24 h-24 bg-cyan-500/20 rounded-full blur-xl" />
                <div className="absolute -bottom-4 -left-4 w-20 h-20 bg-blue-600/20 rounded-full blur-xl" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MobileApp;
